import { Component, OnDestroy, OnInit } from '@angular/core';
import { BreadcrumbService } from '@app/shared/breadcrumbs/breadcrumbs.service';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Breadcrumb } from '@app/models/breadcrumb.model';
import { CompanyDirectoryApiService } from '@app/shared/api/company-directory.api.service';
import { CompanyDirectory } from '@app/models/company/company-directory/CompanyDirectory';
import { SwalUtils } from '@app/shared/utils/swal.utils';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { IState } from '@app/models/state/state.model';
import { ButtonType } from '@app/shared/components/inputs/button/button.component';
import { IconHoverColor } from '@app/shared/components/inputs/table-action-icon/table-action-icon.component';
import { ScimUser } from '@app/models/user/scim-user.model';
import { ScimUserApiService } from '@app/shared/api/scim-user.api.service';
import { PillType } from '@app/shared/components/pill/pill.component';

interface ScimUserPage extends ScimUser {
  managerName: string;
}

@Component({
  selector: 'app-integration-active-directory',
  templateUrl: './integration-active-directory.component.html',
  styleUrls: ['./integration-active-directory.component.scss']
})
export class IntegrationActiveDirectoryComponent implements OnInit, OnDestroy {
  private readonly ngUnsubscribe$: Subject<void> = new Subject<void>();
  public readonly eButtonType = ButtonType;
  public readonly eIconHoverColor = IconHoverColor;
  public readonly ePillType = PillType;

  breadcrumb: Breadcrumb;
  state: IState;
  stateProvisionedUsers: IState;

  companyDirectories: CompanyDirectory[];
  scimUsers: {
    master: ScimUserPage[];
    page: ScimUserPage[];
  };

  constructor(
    private readonly breadcrumbService: BreadcrumbService,
    public readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly companyDirectoryApiService: CompanyDirectoryApiService,
    private readonly scimUserApiService: ScimUserApiService,
    private swalUtils: SwalUtils
  ) {
    this.breadcrumb = undefined;
    this.state = {
      loading: true,
      error: false,
      errorMessage: ''
    };
    this.stateProvisionedUsers = {
      loading: true,
      error: false,
      errorMessage: ''
    };
    this.companyDirectories = [];
    this.scimUsers = {
      master: [],
      page: []
    };

    this.breadcrumb = this.breadcrumbService.init(this.activatedRoute);

    this.router.events.pipe(takeUntil(this.ngUnsubscribe$)).subscribe(event => {
      if (event instanceof NavigationEnd) {
        if (this.activatedRoute.children.length === 0) {
          this.getData();
        }
      }
    });
  }

  ngOnInit(): void {
    this.getData();
  }

  ngOnDestroy(): void {
    this.breadcrumbService.remove(this.breadcrumb);
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.unsubscribe();
  }

  doError(message: string): void {
    this.state.loading = false;
    this.state.error = true;
    this.state.errorMessage = message;
  }

  getData(): void {
    this.state.loading = true;
    this.companyDirectoryApiService.getCompanyDirectories()
      .toPromise()
      .then(companyDirectories => {
        this.companyDirectories = companyDirectories;
        if (this.companyDirectories.length > 0) {
          this.refreshProvisionedUsers();
        }
        this.state.loading = false;
      })
      .catch(err => this.doError(err.message));
  }

  refreshProvisionedUsers(): void {
    this.stateProvisionedUsers.loading = true;

    this.scimUserApiService.getScimUsersForCompany()
      .toPromise()
      .then(scimUsers => {
        scimUsers = this.sortScimUsers(scimUsers);
        const scimUsersParsed = this.parseScimUsersToScimUserPages(scimUsers);
        this.scimUsers = {
          master: scimUsersParsed,
          page: []
        };
        this.stateProvisionedUsers.loading = false;
      })
      .catch(err => {
        this.stateProvisionedUsers.loading = false;
        this.stateProvisionedUsers.error = true;
        this.stateProvisionedUsers.errorMessage = err.message;
      });
  }

  parseScimUsersToScimUserPages(scimUsers: ScimUser[]): ScimUserPage[] {
    const usersIndexedById: { [userId: number]: ScimUser } = {};
    scimUsers.forEach(scimUser => {
      usersIndexedById[scimUser.id] = scimUser;
    });

    const scimUsersParsed: ScimUserPage[] = scimUsers
      .map(scimUser => {
        let userManagerName = 'None';
        if (scimUser.managerScimUserId) {
          const userManager = scimUser.managerScimUserId ? usersIndexedById[scimUser.managerScimUserId] : null;
          userManagerName = userManager ? `${userManager.firstName} ${userManager.lastName}` : '';
        }
        const scimUserParsed: ScimUserPage = {
          ...scimUser,
          managerName: userManagerName
        };
        return scimUserParsed;
      });

    return scimUsersParsed;
  }

  sortScimUsers(scimUsers: ScimUser[]): ScimUser[] {
    return scimUsers
      // Sort by firstName and lastName alphabetically
      .sort((a, b) => {
        const aName = `${a.firstName} ${a.lastName}`;
        const bName = `${b.firstName} ${b.lastName}`;
        return aName.localeCompare(bName);
      })
      // Sort all errors to the top of all users
      .sort((a, b) => {
        const aError = a.error ? 1 : 0;
        const bError = b.error ? 1 : 0;
        return bError - aError;
      });
  }

  deleteCompanyDirectory(companyDirectory: CompanyDirectory): void {
    this.swalUtils.displayWarningConfirmationSwal().then(result => {
      if (result.isConfirmed) {
        this.state.loading = true;
        this.companyDirectoryApiService.deleteCompanyDirectoryById(companyDirectory.id)
          .toPromise()
          .then(() => {
            this.companyDirectories = this.companyDirectories.filter(cd => cd.id !== companyDirectory.id);
            this.state.loading = false;
          })
          .catch(err => this.doError(err.message));
      }
    });
  }

  navigateToCreateDirectory(): void {
    this.router.navigate([
      './create-directory'
    ], {
      relativeTo: this.activatedRoute
    });
  }
}
